import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, Play, BookOpen, Clock, Lock, CheckCircle2, Star, Flame,
  Trophy, TrendingUp, Target, Rocket, Box, Layout as LayoutIcon, DollarSign, Cloud, Users, ChevronRight, X,
} from "lucide-react";
import { Layout } from "../components/Layout";
import { LessonModal } from "../components/learn/LessonModal";
import { learnCatalog, learnProgress, learnLesson } from "../lib/api";
import { useAuth } from "../hooks/useAuth";

const CAT_ICON = { rocket: Rocket, check: CheckCircle2, box: Box, layout: LayoutIcon, dollar: DollarSign, cloud: Cloud, users: Users, sparkles: Sparkles };

const Ring = ({ pct, label }) => {
  const r = 52, c = 2 * Math.PI * r, off = c - (pct / 100) * c;
  return (
    <div className="relative w-32 h-32">
      <svg width="128" height="128" className="-rotate-90">
        <circle cx="64" cy="64" r={r} fill="none" stroke="#f1eddb" strokeWidth="10" />
        <circle cx="64" cy="64" r={r} fill="none" stroke="#D4AF37" strokeWidth="10" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .8s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-2xl font-extrabold text-[#0F0F0F]">{pct}%</span>
        {label && <span className="text-[10px] text-[#9aa1ab]">{label}</span>}
      </div>
    </div>
  );
};

export default function Learn() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [progress, setProgress] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [activeCat, setActiveCat] = useState(null);

  const loadProgress = useCallback(() => {
    learnProgress().then(setProgress).catch(() => {});
  }, []);

  useEffect(() => { learnCatalog().then(setCatalog).catch(() => {}); }, []);
  useEffect(() => { loadProgress(); }, [loadProgress, user]);

  const nextId = progress?.next_lesson_id;
  useEffect(() => {
    if (!nextId) { setFeatured(null); return; }
    learnLesson(nextId).then(setFeatured).catch(() => setFeatured(null));
  }, [nextId]);

  const done = useMemo(() => new Set(progress?.completed || []), [progress]);
  const unlocked = useMemo(() => new Set(progress?.unlocked || []), [progress]);
  const isDone = (id) => done.has(id);
  const isUnlocked = (id) => unlocked.has(id) || done.has(id);

  const allLessons = useMemo(() => {
    if (!catalog) return [];
    const map = {};
    catalog.categories.forEach((c) => c.lessons.forEach((l) => { map[l.id] = { ...l, category: c.slug, catName: c.name, color: c.color }; }));
    return (catalog.global_order || []).map((id) => map[id]).filter(Boolean);
  }, [catalog]);

  const openLesson = (id) => {
    if (!isUnlocked(id)) { toast.info("Complete the previous lesson to unlock this one."); return; }
    setActiveLesson(id);
  };

  const onCompleted = (prog) => { setProgress(prog); };

  if (!catalog || !progress) {
    return <Layout><div className="max-w-7xl mx-auto px-6 py-24 text-center text-[#687280]">Loading Academy…</div></Layout>;
  }

  // Recommended = next few lessons from current point.
  const startIdx = Math.max(0, allLessons.findIndex((l) => l.id === nextId));
  const recommended = (nextId ? allLessons.slice(startIdx, startIdx + 4) : allLessons.slice(0, 4));

  // Category completion.
  const catDone = (c) => c.lessons.filter((l) => done.has(l.id)).length;

  // Path stage status.
  const pathStages = catalog.path.map((stage) => {
    const cat = catalog.categories.find((c) => c.slug === stage.slug);
    const total = cat?.lessons.length || 0;
    const d = cat ? cat.lessons.filter((l) => done.has(l.id)).length : 0;
    return { ...stage, total, d, complete: total > 0 && d === total };
  });
  const currentStageIdx = pathStages.findIndex((s) => !s.complete);

  // Next goal lessons remaining.
  const perLesson = 25;
  const remainingToLevel = progress.next_level ? Math.max(0, Math.ceil((progress.xp_for_next - progress.xp_into_level) / perLesson)) : 0;

  return (
    <Layout>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-8">
        <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-stretch">
          <div className="rounded-3xl bg-white border border-gray-100 p-8 lg:p-10 flex flex-col justify-center">
            <p className="text-[11px] font-bold uppercase tracking-widest text-[#B8901F]">LaunchBS Academy</p>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0F0F0F] mt-3 leading-[1.05]">Learn Startup<br />Fundamentals</h1>
            <p className="text-[#687280] mt-4 max-w-md leading-relaxed">Master the skills every founder needs in just 60 seconds a day. No long videos, no boring courses — just practical knowledge you can apply immediately.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={() => nextId ? openLesson(nextId) : toast.success("You've completed every lesson! 🎉")} className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-7 py-3.5 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="learn-continue-btn">
                <Play size={16} /> Continue Learning
              </button>
              <button onClick={() => document.getElementById("learn-categories")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center justify-center gap-2 border border-gray-200 rounded-full px-7 py-3.5 text-sm font-bold text-[#0F0F0F] hover:border-[#D4AF37] transition-colors" data-testid="learn-browse-btn">
                Browse Lessons
              </button>
            </div>
          </div>

          {/* Progress card */}
          <div className="rounded-3xl bg-gradient-to-br from-[#FBF7EA] to-white border border-[#D4AF37]/30 p-7 flex flex-col" data-testid="learn-progress-card">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#9aa1ab]">Your Progress</p>
              <Star size={16} className="text-[#D4AF37]" />
            </div>
            <p className="font-display text-2xl font-extrabold text-[#0F0F0F] mt-1" data-testid="learn-level">{progress.level} Level</p>
            <div className="flex justify-center my-4"><Ring pct={progress.progress_pct} /></div>
            <div className="text-center">
              <p className="text-sm font-semibold text-[#0F0F0F]" data-testid="learn-completed-count">{progress.completed_count} Lessons Completed</p>
              <div className="h-1.5 rounded-full bg-white/70 overflow-hidden mt-2"><div className="h-full rounded-full bg-[#D4AF37]" style={{ width: `${progress.progress_pct}%` }} /></div>
            </div>
            {progress.next_level && (
              <div className="mt-5 pt-4 border-t border-[#D4AF37]/20 text-center">
                <p className="text-[11px] uppercase tracking-widest text-[#9aa1ab]">Next Level</p>
                <p className="font-display font-bold text-[#8a6d1a] flex items-center justify-center gap-1.5 mt-0.5"><Rocket size={15} /> {progress.next_level}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Recommended */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-14">
        <div className="flex items-center gap-2 mb-1"><Sparkles size={18} className="text-[#D4AF37]" /><h2 className="font-display text-xl font-extrabold text-[#0F0F0F]">Learn What Your Startup Needs</h2></div>
        <p className="text-sm text-[#687280] mb-5">Recommended lessons to move you forward next.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {recommended.map((l) => (
            <button key={l.id} onClick={() => openLesson(l.id)} className="rounded-2xl border border-gray-100 bg-white p-5 text-left hover:border-[#D4AF37]/50 hover:shadow-[0_8px_24px_rgb(0,0,0,0.05)] hover:-translate-y-0.5 transition-all group" data-testid={`learn-reco-${l.id}`}>
              <div className="flex items-center justify-between">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${l.color}1a`, color: l.color }}>
                  {isDone(l.id) ? <CheckCircle2 size={18} /> : isUnlocked(l.id) ? <BookOpen size={18} /> : <Lock size={18} />}
                </span>
                <ArrowRight size={16} className="text-[#9aa1ab] group-hover:translate-x-0.5 transition-transform" />
              </div>
              <p className="font-semibold text-sm text-[#0F0F0F] mt-3 leading-snug">{l.title}</p>
              <p className="text-[11px] text-[#9aa1ab] mt-1 flex items-center gap-1"><Clock size={11} /> {l.duration} · {l.catName}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section id="learn-categories" className="max-w-7xl mx-auto px-6 lg:px-8 pt-14">
        <h2 className="font-display text-2xl font-extrabold text-[#0F0F0F] mb-6">Browse Learning Categories</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {catalog.categories.map((c) => {
            const Icon = CAT_ICON[c.icon] || BookOpen;
            const d = catDone(c);
            const pct = c.lesson_count ? Math.round((d / c.lesson_count) * 100) : 0;
            return (
              <button key={c.slug} onClick={() => setActiveCat(c)} className="rounded-2xl border border-gray-100 bg-white p-5 text-center hover:border-[#D4AF37]/50 hover:shadow-[0_8px_24px_rgb(0,0,0,0.05)] hover:-translate-y-0.5 transition-all" data-testid={`learn-cat-${c.slug}`}>
                <span className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center" style={{ background: `${c.color}1a`, color: c.color }}><Icon size={24} strokeWidth={1.8} /></span>
                <p className="font-display font-bold text-[#0F0F0F] mt-3">{c.name}</p>
                <p className="text-xs text-[#687280] mt-1 min-h-[32px]">{c.desc}</p>
                <p className="text-[11px] font-semibold text-[#9aa1ab] mt-2">{c.lesson_count} Lessons</p>
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mt-2"><div className="h-full rounded-full bg-[#D4AF37]" style={{ width: `${pct}%` }} /></div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Path + Lesson of the Day */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-14">
        <div className="grid lg:grid-cols-[380px_1fr] gap-6">
          {/* Learning Path */}
          <div className="rounded-3xl bg-white border border-gray-100 p-6" data-testid="learn-path">
            <h3 className="font-display text-lg font-extrabold text-[#0F0F0F] mb-4">Your Learning Path</h3>
            <div className="space-y-2">
              {pathStages.map((s, i) => {
                const status = s.complete ? "complete" : i === currentStageIdx ? "current" : "locked";
                const cat = catalog.categories.find((c) => c.slug === s.slug);
                return (
                  <button key={s.slug} onClick={() => cat && setActiveCat(cat)} className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${status === "current" ? "bg-[#FBF7EA] border border-[#D4AF37]/40" : "hover:bg-gray-50 border border-transparent"}`} data-testid={`learn-path-${i}`}>
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${status === "complete" ? "bg-green-500 text-white" : status === "current" ? "bg-[#D4AF37] text-[#0F0F0F]" : "bg-gray-100 text-[#9aa1ab]"}`}>
                      {status === "complete" ? <CheckCircle2 size={16} /> : status === "locked" ? <Lock size={13} /> : i + 1}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className={`block font-semibold text-sm ${status === "locked" ? "text-[#9aa1ab]" : "text-[#0F0F0F]"}`}>{i + 1}. {s.name}</span>
                      <span className="block text-[11px] text-[#9aa1ab]">{s.d}/{s.total} lessons</span>
                    </span>
                    <span className={`text-[10px] font-bold ${status === "complete" ? "text-green-600" : status === "current" ? "text-[#8a6d1a]" : "text-[#9aa1ab]"}`}>
                      {status === "complete" ? "Completed" : status === "current" ? "Current" : "Locked"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Lesson of the Day */}
          <div className="rounded-3xl bg-gradient-to-b from-[#161514] to-[#0F0F0F] text-white p-7 flex flex-col" data-testid="learn-lotd">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#D4AF37]">Lesson of the Day</p>
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-xs"><Clock size={12} /> 1 Minute Lesson</span>
            </div>
            {featured ? (
              <>
                <h3 className="font-display text-3xl font-extrabold mt-4">{featured.title}</h3>
                <p className="text-white/70 mt-2 leading-relaxed">{featured.definition}</p>
                <div className="grid sm:grid-cols-3 gap-4 mt-6">
                  <div><p className="text-[11px] font-bold uppercase text-[#D4AF37] mb-1">Example</p><p className="text-sm text-white/70 leading-snug">{featured.example}</p></div>
                  <div><p className="text-[11px] font-bold uppercase text-emerald-400 mb-1">Founder Tip</p><p className="text-sm text-white/70 leading-snug">{featured.tip}</p></div>
                  <div><p className="text-[11px] font-bold uppercase text-pink-400 mb-1">Your Action</p><p className="text-sm text-white/70 leading-snug">{featured.action}</p></div>
                </div>
                <button onClick={() => openLesson(featured.id)} className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3.5 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="learn-lotd-start">
                  Start Lesson <ArrowRight size={16} />
                </button>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
                <Trophy size={40} className="text-[#D4AF37]" />
                <p className="font-display text-2xl font-bold mt-4">You've finished every lesson!</p>
                <p className="text-white/50 mt-2">More lessons are on the way.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Journey stats */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-14 pb-4">
        <h2 className="font-display text-xl font-extrabold text-[#0F0F0F] mb-4">Your Learning Journey</h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat icon={Trophy} tint="bg-[#FBF7EA] text-[#B8901F]" value={progress.xp} label="Total XP" testid="stat-xp" />
          <Stat icon={Flame} tint="bg-orange-50 text-orange-500" value={progress.streak} label="Day Streak" testid="stat-streak" />
          <Stat icon={BookOpen} tint="bg-indigo-50 text-indigo-600" value={progress.completed_count} label="Lessons Completed" testid="stat-completed" />
          <Stat icon={TrendingUp} tint="bg-emerald-50 text-emerald-600" value={progress.level} label="Current Level" small testid="stat-level" />
          <Stat icon={Target} tint="bg-pink-50 text-pink-500" value={progress.next_level ? `${remainingToLevel} more` : "Maxed"} label={progress.next_level ? `to ${progress.next_level}` : "Top level"} small testid="stat-goal" />
        </div>
      </section>

      {/* Category lessons modal */}
      {activeCat && (
        <CategoryModal cat={activeCat} done={done} isUnlocked={isUnlocked} onClose={() => setActiveCat(null)} onOpenLesson={(id) => { setActiveCat(null); openLesson(id); }} />
      )}

      <LessonModal lessonId={activeLesson} isDone={activeLesson ? isDone(activeLesson) : false} onClose={() => setActiveLesson(null)} onCompleted={onCompleted} />
    </Layout>
  );
}

const Stat = ({ icon: Icon, tint, value, label, small, testid }) => (
  <div className="rounded-2xl bg-white border border-gray-100 p-4 flex items-center gap-3" data-testid={testid}>
    <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tint}`}><Icon size={20} /></span>
    <div className="min-w-0">
      <p className={`font-display font-extrabold text-[#0F0F0F] ${small ? "text-base" : "text-2xl"} truncate`}>{value}</p>
      <p className="text-[11px] text-[#687280] truncate">{label}</p>
    </div>
  </div>
);

const CategoryModal = ({ cat, done, isUnlocked, onClose, onOpenLesson }) => (
  <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="learn-cat-modal">
    <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-display text-2xl font-extrabold text-[#0F0F0F]">{cat.name}</h2>
          <p className="text-sm text-[#687280] mt-1">{cat.desc}</p>
        </div>
        <button onClick={onClose} className="text-[#687280] hover:text-[#0F0F0F] shrink-0 ml-2" data-testid="learn-cat-close"><X size={20} /></button>
      </div>
      <div className="mt-5 space-y-2.5">
        {cat.lessons.map((l, i) => {
          const d = done.has(l.id), u = isUnlocked(l.id);
          return (
            <button key={l.id} onClick={() => onOpenLesson(l.id)} disabled={!u} className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${d ? "border-green-200 bg-green-50/50" : u ? "border-gray-100 hover:border-[#D4AF37]/50" : "border-gray-100 opacity-60 cursor-not-allowed"}`} data-testid={`learn-cat-lesson-${l.id}`}>
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${d ? "bg-green-500 text-white" : u ? "bg-[#0F0F0F] text-[#D4AF37]" : "bg-gray-100 text-[#9aa1ab]"}`}>
                {d ? <CheckCircle2 size={16} /> : u ? i + 1 : <Lock size={13} />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-sm text-[#0F0F0F]">{l.title}</span>
                <span className="block text-[11px] text-[#9aa1ab] flex items-center gap-1"><Clock size={11} /> {l.duration} · {l.difficulty} · +{l.xp} XP</span>
              </span>
              <ChevronRight size={16} className="text-[#9aa1ab] shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  </div>
);
